import { Button, Divider, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { usersCreateAnonymous } from 'api/auth_endpoints/authFromFileSpecWithTransformer'
import { AnonymousUser, Twin, TwinConversation, TwinMessage } from 'api/model'
import { useFetchAnonymousTwin, useFetchAnonymousUser } from 'hooks/use-twin'
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSetRecoilState } from 'recoil'
import { toastAtom } from 'state'
import { isFieldEmpty } from 'utils'
import { ReactComponent as JelledLogo } from 'components/NavBar/jelled-final-logo.svg'
import ProfilePhoto from 'components/ProfilePhoto'
import { useAnonymousAuthToken } from 'hooks/use-auth-token'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import InsertLinkOutlinedIcon from '@mui/icons-material/InsertLinkOutlined'
import {
  SuggestedChatBubble,
  TwinChatBubble,
  TwinPhoto,
  UserChatBubble,
  getTextWithLinks,
  streamedTwinsAsk,
} from './Conversations'
import { LoadingButton } from '@mui/lab'
import SendIcon from '@mui/icons-material/Send'
import { twinsConversationClear } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { useFetchConversationMessages } from 'hooks/use-messages'
import { useFetchAnonymousRecentConversations } from 'hooks/use-conversation'
import Toast from 'components/AuthenticatedContainer/Toast'
import UnauthorizedUserAlert from 'components/UnauthorizedUserAlert/UnauthorizedUserAlert'
import { useFetchSuggestedQuestions } from 'hooks/use-instructions'
import CloseOutlined from '@mui/icons-material/CloseOutlined'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import { format } from 'date-fns'
import { TwinChatBubbleStack } from 'components/Chatbot'
import { COLORS } from 'app-constants'

const AuthForm = ({
  setAnonymousUser,
  shortUrl,
  twinId,
  twinOrgId,
}: {
  setAnonymousUser: (anonymousUser: AnonymousUser | null) => void
  shortUrl: string
  twinId: string
  twinOrgId: string
}) => {
  const setToast = useSetRecoilState(toastAtom)
  const [, setAnonymousAuthToken] = useAnonymousAuthToken()

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [submitCount, setSubmitCount] = useState<number>(0)

  const [email, setEmail] = useState<string>('')
  const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
  }

  const [name, setName] = useState<string>('')
  const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
  }

  const createAnonymousUser = async () => {
    setSubmitCount(submitCount + 1)
    setIsSubmitting(true)

    if (isFieldEmpty(email) || isFieldEmpty(name) || !shortUrl || !twinId || !twinOrgId) {
      setIsSubmitting(false)
      return
    }

    try {
      const response = await usersCreateAnonymous({
        email,
        name,
        short_url: shortUrl,
        twin_id: twinId,
        organization_id: twinOrgId,
      })
      setAnonymousUser(response.data)

      // JWT token must be set as it is used in use-auth-token.ts to make API calls
      if (response.data.token) {
        setAnonymousAuthToken(response.data.token)
      }

      setToast({ title: 'Successfully started anonymous conversation', severity: 'success' })
    } catch (Error) {
      setToast({ title: 'Failed to start anonymous conversation', severity: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Stack width={240}>
      <TextField
        className="has-label"
        label="Name"
        required={true}
        placeholder="First Last"
        value={name}
        onChange={onNameChange}
        disabled={isSubmitting}
        error={submitCount > 0 && isFieldEmpty(name)}
        sx={{
          marginBottom: '36px',
        }}
      />

      <TextField
        className="has-label"
        label="Email"
        required={true}
        placeholder="Enter your email"
        value={email}
        onChange={onEmailChange}
        disabled={isSubmitting}
        error={submitCount > 0 && isFieldEmpty(email)}
        sx={{
          marginBottom: '24px',
        }}
      />

      <Button variant="contained" color="primary" disabled={isSubmitting} onClick={createAnonymousUser}>
        Start conversation
      </Button>
    </Stack>
  )
}

const CTAButtons = ({
  setIsCTAButtonsVisible,
}: {
  setIsCTAButtonsVisible?: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const isMobile = useCheckMobileScreen()

  return (
    <Stack
      borderRadius="16px"
      mt="12px"
      mx="12px"
      py={2}
      px={1}
      justifyContent="center"
      alignItems="center"
      sx={{
        background: isMobile
          ? 'var(--Subtle-gradient, linear-gradient(156deg, #00C3F0 -43.46%, #0F6AFA 49.07%, #115DFC 103.6%))'
          : 'transparent',
      }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <Typography
          variant="h6"
          sx={{
            color: COLORS.PURE_WHITE,
          }}>
          Interested in creating your own digital twin?
        </Typography>

        {isMobile && (
          <IconButton
            onClick={() => {
              setIsCTAButtonsVisible?.(false)
            }}
            sx={{
              width: '16px',
              height: '16px',
            }}>
            <CloseOutlined
              sx={{
                color: COLORS.PURE_WHITE,
              }}
            />
          </IconButton>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={2}>
        <Button
          variant="contained"
          onClick={() => {
            window.open('https://www.jelled.ai', '_blank')
          }}
          sx={{
            backgroundColor: COLORS.GRAY_6,
            color: COLORS.GRAY_0,
            // add hover color
            '&:hover': {
              backgroundColor: COLORS.GRAY_5,
            },
          }}>
          Learn more
        </Button>

        <Button
          variant="contained"
          onClick={() => {
            window.open('https://app.jelled.ai', '_blank')
          }}
          sx={{
            backgroundColor: COLORS.GRAY_0,
            color: COLORS.PURE_WHITE,
            // add hover color
            '&:hover': {
              backgroundColor: COLORS.GRAY_1,
            },
          }}>
          Sign up
        </Button>
      </Stack>
    </Stack>
  )
}

const ConversationView = ({
  anonymousTwin,
  twinId,
  twinOrgId,
  anonymousUser,
}: {
  anonymousTwin: Twin | null
  anonymousUser: AnonymousUser
  twinId: string
  twinOrgId: string
}) => {
  const isMobile = useCheckMobileScreen()
  const setToast = useSetRecoilState(toastAtom)

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [conversationId, setConversationId] = useState<string>('')

  const { recentConversations, error, setRecentConversations } = useFetchAnonymousRecentConversations(twinOrgId, twinId)

  const conversation = recentConversations
    .map((rc) => rc.twin_conversations)
    .flat()
    .find((c) => c.id === conversationId)

  // if we have a recent conversation, we should be using that conversation id when calling twinsAsk
  useEffect(() => {
    if (recentConversations.length > 0 && !conversationId) {
      setConversationId(recentConversations[0].twin_conversations[0].id)
    }
  }, [recentConversations, conversationId])

  const [isCreatingNewConversation, setIsCreatingNewConversation] = useState<boolean>(false)

  const { conversationMessages, setConversationMessages } = useFetchConversationMessages(twinId ?? '', conversationId, {
    twinOrgId,
    isCreatingNewConversation,
  })

  const { suggestedQuestions } = useFetchSuggestedQuestions(
    anonymousTwin?.id ?? '',
    anonymousTwin?.organization_id ?? ''
  )

  const [question, setQuestion] = useState<string>('')
  const onQuestionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setQuestion(event.target.value)
  }

  const listRef = useRef<HTMLDivElement>(null)
  const scrollToBottom = () => {
    const list = listRef.current
    if (list) {
      list.scrollTop = list.scrollHeight
    }
  }

  // it should only be visible as a header on mobile
  const [isCTAButtonsVisible, setIsCTAButtonsVisible] = useState<boolean>(isMobile)

  // scroll to the bottom of the messages when the component mounts
  useEffect(() => {
    scrollToBottom()
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [conversationMessages])

  const [streamedMessage, setStreamedMessage] = useState<string>('')
  useEffect(() => {
    // debounce scroll by 100ms
    const timeout = setTimeout(() => {
      scrollToBottom()
    }, 10)

    return () => {
      clearTimeout(timeout)
    }
  }, [streamedMessage])

  const [tempTwinMessage, setTempTwinMessage] = useState<TwinMessage | null>(null)
  useEffect(() => {
    if (tempTwinMessage) {
      setConversationMessages((prevMessages) => [...prevMessages, tempTwinMessage])
      setTempTwinMessage(null)
    }
  }, [tempTwinMessage, setConversationMessages])

  const [pendingTwinMessage, setPendingTwinMessage] = useState<TwinMessage | null>(null)

  const [tempTwinConversation, setTempTwinConversation] = useState<TwinConversation | null>(null)
  useEffect(() => {
    if (tempTwinConversation) {
      // if we don't yet have a conversation in recoil
      if (recentConversations.length === 0) {
        setRecentConversations((prev) => {
          if (!anonymousTwin || !prev) {
            return []
          }

          return [
            {
              twin: anonymousTwin,
              twin_conversations: [tempTwinConversation],
            },
          ]
        })
      } else {
        // loop through the conversations and find the one that matches the twin id
        const updatedConversations = recentConversations.map((conversation) => {
          if (conversation.twin.id === tempTwinConversation?.twin_id) {
            return {
              ...conversation,
              twin_conversations: [tempTwinConversation],
            }
          }
          return conversation
        })
        setRecentConversations(updatedConversations)
      }
      setTempTwinConversation(null)
    }
    // we don't need recentConversations in the dependency array because we will have an infinite loop
    // where recentConversations keeps changing and we never get to set tempTwinConversation to be null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempTwinConversation, setRecentConversations])

  const onSendAnonymousQuestion = async (retriedQuestion?: string) => {
    if (!question && !retriedQuestion) {
      return
    }

    try {
      setIsSubmitting(true)

      if (!conversation) {
        setIsCreatingNewConversation?.(true)
      }

      await streamedTwinsAsk(
        twinOrgId,
        twinId,
        question,
        false,
        setPendingTwinMessage,
        setTempTwinMessage,
        setStreamedMessage,
        setTempTwinConversation,
        false,
        {
          setConversationId,
          retriedQuestion,
          conversation,
        }
      )

      setQuestion('')
    } catch (error) {
      setToast({ title: 'Failed to send question', severity: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const onHydrateConversationMessage = (twinMessage: TwinMessage) => {
    setConversationMessages((prevMessages) => {
      const updatedMessages = [...prevMessages]
      const messageIndex = updatedMessages.findIndex((message) => message.id === twinMessage.id)
      updatedMessages[messageIndex] = twinMessage
      return updatedMessages
    })
  }

  const onCopySharedLink = () => {
    navigator.clipboard.writeText(window.location.href)
    setToast({ title: 'Copied shared link to clipboard', severity: 'success' })
  }

  const onSuggestionClick = (text: string) => {
    onSendAnonymousQuestion(text)
  }

  const onRetryClick = async (question: string) => {
    onSendAnonymousQuestion(question)
  }

  const onRefreshClick = async () => {
    try {
      await twinsConversationClear(twinOrgId, conversationId, {
        twin_id: twinId,
      })
      // clear the conversation messages
      setConversationMessages([])
      setConversationId('')
      setToast({
        severity: 'success',
        title: 'Successfully refreshed conversation',
      })
    } catch (error) {
      setToast({
        severity: 'error',
        title: 'Failed to refresh conversation',
      })
    }
  }

  return (
    <Stack>
      {error && <UnauthorizedUserAlert />}

      {isMobile && isCTAButtonsVisible && <CTAButtons setIsCTAButtonsVisible={setIsCTAButtonsVisible} />}

      <Stack direction="row">
        {!isMobile && (
          <Stack
            justifyContent="center"
            alignItems="center"
            direction="column"
            sx={{
              height: '100vh',
              width: '50vw',
              background:
                'var(--Subtle-gradient, linear-gradient(156deg, #00C3F0 -43.46%, #0F6AFA 49.07%, #115DFC 103.6%))',
            }}>
            <Stack
              onClick={() => {
                // open the jelled website in a new tab
                window.open('https://www.jelled.ai', '_blank')
              }}
              sx={{
                cursor: 'pointer',
              }}>
              <JelledLogo
                style={{
                  width: '126px',
                  height: '30px',
                }}
              />
            </Stack>

            <CTAButtons />
          </Stack>
        )}

        <Stack
          height={isCTAButtonsVisible && isMobile ? 'calc(100vh - 124px)' : '100vh'}
          width={isMobile ? '100%' : '50vw'}
          px={4}
          pt={2}
          pb={4}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack spacing={1} direction="row" alignItems="center">
              <ProfilePhoto
                src={anonymousTwin?.picture}
                size="32px"
                name={anonymousTwin?.name || anonymousTwin?.user.email}
              />
              <Typography variant="h6">{anonymousTwin?.name}</Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Tooltip title="Clear your conversation messages" enterTouchDelay={0}>
                <IconButton onClick={onRefreshClick} size="small">
                  <RefreshOutlinedIcon
                    sx={{
                      color: COLORS.GRAY_3,
                    }}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title="Copy shared link to twin" enterTouchDelay={0}>
                <IconButton onClick={onCopySharedLink}>
                  <InsertLinkOutlinedIcon
                    sx={{
                      color: COLORS.GRAY_3,
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          <Divider
            sx={{
              my: 2,
            }}
          />

          <Stack
            p={2}
            borderRadius="10px"
            sx={{
              backgroundColor: COLORS.GRAY_8,
            }}>
            <Typography
              variant="pS"
              sx={{
                color: COLORS.GRAY_2,
              }}>
              {getTextWithLinks(anonymousTwin?.public_summary ?? '')}
            </Typography>
          </Stack>

          <Stack
            ref={listRef}
            sx={{
              overflowY: 'scroll',
              height: '100%',
              scrollBehavior: 'smooth',
            }}>
            <Stack direction="row" alignItems="center" spacing={1} mt={3} mb={2}>
              <ProfilePhoto
                src={anonymousTwin?.picture || anonymousTwin?.user.picture}
                size="24px"
                name={anonymousTwin?.name || `${anonymousTwin?.user.name}'s twin`}
              />
              <TwinChatBubbleStack
                direction="row"
                alignItems="center"
                sx={{
                  p: '12px',
                  backgroundColor: COLORS.GRAY_8,
                }}>
                <Typography variant="h6">Hi! How can I help you today?</Typography>
              </TwinChatBubbleStack>
            </Stack>

            {suggestedQuestions.map((sq, idx) => {
              return <SuggestedChatBubble key={idx} text={sq} onSuggestionClick={onSuggestionClick} />
            })}

            {conversationMessages.map((cm) => (
              <Stack key={cm.id} mb={2}>
                <UserChatBubble
                  shouldRetry={!cm.twin_answer}
                  anonymousUser={anonymousUser}
                  twinMessage={cm}
                  onRetryClick={onRetryClick}
                />
                {cm.twin_answer && (
                  <TwinChatBubble
                    twinOrgId={twinOrgId}
                    hydrateConversationMessage={onHydrateConversationMessage}
                    twinMessage={cm}
                  />
                )}
              </Stack>
            ))}

            {pendingTwinMessage?.date_created && (
              <Typography variant="h7" textAlign={'center'} color="#666666" mb={3}>
                {format(new Date(pendingTwinMessage.date_created), 'MMM dd, yyyy')} at{' '}
                {format(new Date(pendingTwinMessage.date_created), 'hh:mm a')}
              </Typography>
            )}

            {pendingTwinMessage && ( // if there is a streamed message, we should also show the user's question
              <Stack
                borderRadius="8px"
                alignSelf="flex-end"
                width="fit-content"
                maxWidth="50%"
                p={2}
                mb={2}
                sx={{
                  backgroundColor: COLORS.GRAY_8,
                }}>
                <Typography variant="pS" textAlign="right">
                  {pendingTwinMessage.question}
                </Typography>
              </Stack>
            )}

            {streamedMessage && (
              <Stack>
                <TwinChatBubbleStack
                  sx={{
                    width: isMobile ? 'auto' : 'fit-content',
                    backgroundColor: COLORS.GRAY_8,
                  }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    {anonymousTwin && <TwinPhoto twin={anonymousTwin} anonymousTwin={anonymousTwin || null} />}
                  </Stack>

                  <Typography
                    variant="pS"
                    sx={{
                      p: '12px',
                      whiteSpace: 'pre-line',
                    }}>
                    {getTextWithLinks(streamedMessage)}
                  </Typography>
                </TwinChatBubbleStack>
              </Stack>
            )}
          </Stack>

          <Stack>
            <TextField
              placeholder="What would you like to know?"
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  onSendAnonymousQuestion()
                }
              }}
              value={question}
              onChange={onQuestionChange}
              disabled={isSubmitting}
              fullWidth
              InputProps={{
                endAdornment: (
                  <LoadingButton
                    loading={isSubmitting}
                    disabled={!question}
                    color="primary"
                    onClick={() => {
                      onSendAnonymousQuestion()
                    }}
                    style={{
                      marginRight: '8px',
                    }}>
                    <SendIcon
                      style={{
                        height: '36px',
                        width: '36px',
                        padding: '6px',
                        borderRadius: '6px',
                        background: isSubmitting || question ? COLORS.JELLED_PRIMARY_BLUE : 'transparent',
                        fill: isSubmitting ? 'transparent' : question ? COLORS.PURE_WHITE : COLORS.GRAY_3,
                        transition: 'background 0.5s, fill 0.5s',
                      }}
                    />
                  </LoadingButton>
                ),
              }}
              sx={{
                marginTop: '32px',
                input: {
                  color: 'secondary.main',
                },
                '& ::placeholder': {
                  color: 'secondary.main',
                },
              }}
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  )
}

const TwinInfo = ({ anonymousTwin }: { anonymousTwin: Twin | null }) => {
  if (!anonymousTwin) {
    return null
  }

  return (
    <Stack justifyContent="center" alignItems="left" maxWidth="320px" mb="37px">
      <Typography mb={1} variant="body1W" textAlign="center">
        Start a conversation with {anonymousTwin.user.name}'s twin
      </Typography>

      <Stack alignItems="center" spacing={1} mb={1} mt="23px">
        <ProfilePhoto src={anonymousTwin.picture} size="32px" name={anonymousTwin.name || anonymousTwin.user.email} />
        <Typography variant="h6">{anonymousTwin.name}</Typography>
      </Stack>
      <Typography
        variant="pS"
        color="#666666"
        textAlign="center"
        sx={{
          whiteSpace: 'pre-line',
        }}>
        {getTextWithLinks(anonymousTwin?.public_summary ?? '')}
      </Typography>
    </Stack>
  )
}

const AppHeader = () => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing="8px"
      mb={4}
      position="fixed"
      top="32px"
      onClick={() => {
        // open the jelled website in a new tab
        window.open('https://www.jelled.ai', '_blank')
      }}
      sx={{
        cursor: 'pointer',
      }}>
      <JelledLogo
        style={{
          width: '126px',
          height: '30px',
        }}
      />
    </Stack>
  )
}

const AnonymousConversation = () => {
  const isMobile = useCheckMobileScreen()

  const { shortUrl = '' } = useParams()

  const { anonymousTwin, isLoading } = useFetchAnonymousTwin(shortUrl)
  const [getAnonymousUser, setAnonymousUser] = useFetchAnonymousUser()
  const anonymousUser = getAnonymousUser()

  // TODO: add loading state
  if (isLoading) {
    return null
  }

  if (!anonymousUser) {
    return (
      <Stack width="100%" height="100vh" pl={isMobile ? 0 : 4} pt={4} justifyContent="center" alignItems="center">
        <AppHeader />
        <Stack mb={2}>
          <TwinInfo anonymousTwin={anonymousTwin} />
        </Stack>
        <AuthForm
          setAnonymousUser={setAnonymousUser}
          shortUrl={shortUrl}
          twinId={anonymousTwin?.id ?? ''}
          twinOrgId={anonymousTwin?.organization_id ?? ''}
        />
      </Stack>
    )
  }

  return (
    <Stack width="100%" height="100vh">
      <ConversationView
        twinOrgId={anonymousTwin?.organization_id ?? ''}
        twinId={anonymousTwin?.id ?? ''}
        anonymousTwin={anonymousTwin}
        anonymousUser={anonymousUser}
      />
      <Toast />
    </Stack>
  )
}

export default AnonymousConversation
